import React, {useState, useEffect, useMemo} from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    CircularProgress,
    TableSortLabel,
} from '@mui/material';
import {RenderParticipantName} from "../components/Elements";
import Medal from "../utils/Medal";
import StyledLink from "../utils/StyledLink";
import {fetchPodiumEntries} from "../api/contests";
import { useParticipantRanks } from '../utils/useParticipantRanks';

// Utility to generate consistent colors based on contest name
const generateColor = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${hash % 360}, 70%, 70%)`;
};

interface PodiumEntry {
    contest_id: number;
    participant_id: number;
    contest_name: string;
    participant_name: string;
    profile_picture: string;
    result: string | number;
    result_unit: string;
    grouping: string;
    ranking: number;
    total_participants: number;
    created_at: string;
}

const PodiumPage: React.FC = () => {
    const [entries, setEntries] = useState<PodiumEntry[]>([]);
    const [filteredEntries, setFilteredEntries] = useState<PodiumEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedContest, setSelectedContest] = useState<string>(''); // All contests by default
    const [selectedPeriod, setSelectedPeriod] = useState<string>('7'); // Default 
    const [sortField, setSortField] = useState<keyof PodiumEntry | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const top_entry_count = 3;

    const periods = [
        {value: '', label: 'За всё время'},
        {value: '28', label: 'За 28 дней'},
        {value: '7', label: 'За 7 дней'},
    ];

    const fetchEntries = async (days?: string) => {
        try {
            setLoading(true);
            const entries = await fetchPodiumEntries(top_entry_count, days);
            setEntries(entries);
            setFilteredEntries(entries);
        } catch (err) {
            console.error('Error fetching podium data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntries(selectedPeriod);
    }, [selectedPeriod]);

    const handleSort = (field: keyof PodiumEntry) => {
        const isAsc = sortField === field && sortDirection === 'asc';
        setSortField(field);
        setSortDirection(isAsc ? 'desc' : 'asc');
    };

    const sortedEntries = useMemo(() => {
        if (!sortField) return filteredEntries;

        return [...filteredEntries].sort((a, b) => {
            const aValue = a[sortField] ?? '';
            const bValue = b[sortField] ?? '';
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredEntries, sortField, sortDirection]);

    const contestNames = useMemo(() => {
        return entries.reduce((acc, entry) => {
            const existing = acc.find((item) => item.name === entry.contest_name);
            if (existing) {
                existing.count += 1;
            } else {
                acc.push({name: entry.contest_name, count: 1});
            }
            return acc;
        }, [] as { name: string; count: number }[]);
    }, [entries]);

    const filteredByContest = useMemo(
        () =>
            selectedContest
                ? sortedEntries.filter((entry) => entry.contest_name === selectedContest)
                : sortedEntries,
        [sortedEntries, selectedContest]
    );

    const { ranksByParticipantId } = useParticipantRanks();

    return (
        <Box>
            <Typography variant="h6" gutterBottom color="primary">
                {`Первые ${top_entry_count} места в каждой группе каждой зарубы`}
            </Typography>

            <Box display="flex" mb={2} gap={2}>
                <Select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    displayEmpty
                >
                    {periods.map((period) => (
                        <MenuItem key={period.value} value={period.value}>
                            {period.label}
                        </MenuItem>
                    ))}
                </Select>

                <Select
                    value={selectedContest}
                    onChange={(e) => setSelectedContest(e.target.value)}
                    displayEmpty
                    fullWidth
                >
                    <MenuItem value="">Все зарубы</MenuItem>
                    {contestNames.map((contest) => (
                        <MenuItem key={contest.name} value={contest.name}>
                            <Box display="flex" alignItems="center">
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: generateColor(contest.name),
                                        marginRight: 1,
                                    }}
                                />
                                {contest.name} ({contest.count})
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" mt={2}>
                    <CircularProgress/>
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {!selectedContest && (
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'contest_name'}
                                            direction={sortDirection}
                                            onClick={() => handleSort('contest_name')}
                                        >
                                            Заруба
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                <TableCell width={12}></TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'participant_name'}
                                        direction={sortDirection}
                                        onClick={() => handleSort('participant_name')}
                                    >
                                        Участник
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'grouping'}
                                        direction={sortDirection}
                                        onClick={() => handleSort('grouping')}
                                    >
                                        Группа
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'result'}
                                        direction={sortDirection}
                                        onClick={() => handleSort('result')}
                                    >
                                        Результат
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'total_participants'}
                                        direction={sortDirection}
                                        onClick={() => handleSort('total_participants')}
                                    >
                                        Участников
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'created_at'}
                                        direction={sortDirection}
                                        onClick={() => handleSort('created_at')}
                                    >
                                        Дата
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredByContest.map((entry, index) => (
                                <TableRow key={index}>
                                    {!selectedContest && (
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: generateColor(entry.contest_name),
                                                        marginRight: 1,
                                                    }}
                                                    title={entry.contest_name}
                                                />
                                                <StyledLink to={`/contest/${entry.contest_id}`}>
                                                    {entry.contest_name}
                                                </StyledLink>
                                            </Box>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Medal ranking={entry.ranking}/>
                                    </TableCell>
                                    <TableCell>
                                        {RenderParticipantName(
                                          entry.participant_id,
                                          entry.participant_name,
                                          entry.profile_picture,
                                          true,
                                          ranksByParticipantId.get(entry.participant_id) ?? []
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <StyledLink
                                            to={`/contest/${entry.contest_id}/?tab=1&group=${encodeURIComponent(
                                                entry.grouping
                                            )}`}
                                        >
                                            {entry.grouping}
                                        </StyledLink>
                                    </TableCell>
                                    <TableCell>{entry.result}</TableCell>
                                    <TableCell>{entry.total_participants}</TableCell>
                                    <TableCell>{new Date(entry.created_at).toLocaleString('ru-RU')}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default PodiumPage;
